import * as tslib_1 from "tslib";
import { extend, getGUID, sanitizeGuid, stringIsNullOrEmpty } from "@pnp/common";
import { ClientSvcQueryable, MethodParams, method, objConstructor, objectPath, objectProperties, opQuery, property } from "@pnp/sp-clientsvc";
import { TermGroup, TermGroups } from "./termgroup";
import { Term, Terms } from "./terms";
import { TermSet, TermSets } from "./termsets";
/**
 * Represents the set of available term stores and the collection methods
 */
var TermStores = /** @class */ (function (_super) {
    tslib_1.__extends(TermStores, _super);
    function TermStores(parent) {
        if (parent === void 0) { parent = ""; }
        var _this = _super.call(this, parent) || this;
        _this._objectPaths.add(property("TermStores", 
        // actions
        objectPath()));
        return _this;
    }
    /**
     * Gets the term stores
     */
    TermStores.prototype.get = function () {
        var _this = this;
        return this.sendGetCollection(function (d) {
            if (!stringIsNullOrEmpty(d.Name)) {
                return _this.getByName(d.Name);
            }
            else if (!stringIsNullOrEmpty(d.Id)) {
                return _this.getById(d.Id);
            }
            throw Error("Could not find Name or Id in TermStores.get(). You must include at least one of these in your select fields.");
        });
    };
    /**
     * Returns the TermStore specified by its index name
     *
     * @param name The index name of the TermStore to be returned
     */
    TermStores.prototype.getByName = function (name) {
        return this.getChild(TermStore, "GetByName", MethodParams.build().string(name));
    };
    /**
     * Returns the TermStore specified by its GUID index
     *
     * @param id The GUID index of the TermStore to be returned
     */
    TermStores.prototype.getById = function (id) {
        return this.getChild(TermStore, "GetById", MethodParams.build().string(sanitizeGuid(id)));
    };
    return TermStores;
}(ClientSvcQueryable));
export { TermStores };
var TermStore = /** @class */ (function (_super) {
    tslib_1.__extends(TermStore, _super);
    function TermStore(parent, _objectPaths) {
        if (parent === void 0) { parent = ""; }
        if (_objectPaths === void 0) { _objectPaths = null; }
        return _super.call(this, parent, _objectPaths) || this;
    }
    Object.defineProperty(TermStore.prototype, "hashTagsTermSet", {
        get: function () {
            return this.getChildProperty(TermSet, "HashTagsTermSet");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TermStore.prototype, "keywordsTermSet", {
        get: function () {
            return this.getChildProperty(TermSet, "KeywordsTermSet");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TermStore.prototype, "orphanedTermsTermSet", {
        get: function () {
            return this.getChildProperty(TermSet, "OrphanedTermsTermSet");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TermStore.prototype, "systemGroup", {
        get: function () {
            return this.getChildProperty(TermGroup, "SystemGroup");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TermStore.prototype, "groups", {
        get: function () {
            return this.getChildProperty(TermGroups, "Groups");
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Gets the term store data
     */
    TermStore.prototype.get = function () {
        return this.sendGet(TermStore);
    };
    /**
     * Gets term sets
     *
     * @param name
     * @param lcid
     */
    TermStore.prototype.getTermSetsByName = function (name, lcid) {
        var params = MethodParams.build()
            .string(name)
            .number(lcid);
        return this.getChild(TermSets, "GetTermSetsByName", params);
    };
    /**
     * Provides access to an ITermSet by id
     *
     * @param id
     */
    TermStore.prototype.getTermSetById = function (id) {
        var params = MethodParams.build().string(sanitizeGuid(id));
        return this.getChild(TermSet, "GetTermSet", params);
    };
    /**
     * Provides access to an ITermSet by id
     *
     * @param id
     */
    TermStore.prototype.getTermById = function (id) {
        var params = MethodParams.build().string(sanitizeGuid(id));
        return this.getChild(Term, "GetTerm", params);
    };
    /**
     * Provides access to an ITermSet by id
     *
     * @param id
     */
    TermStore.prototype.getTermsById = function () {
        var ids = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            ids[_i] = arguments[_i];
        }
        var params = MethodParams.build().strArray(ids.map(function (id) { return sanitizeGuid(id); }));
        return this.getChild(Terms, "GetTermsById", params);
    };
    /**
     * Gets a term from a term set based on the supplied ids
     *
     * @param termId Term Id
     * @param termSetId Termset Id
     */
    TermStore.prototype.getTermInTermSet = function (termId, termSetId) {
        var params = MethodParams.build().string(sanitizeGuid(termId)).string(sanitizeGuid(termSetId));
        return this.getChild(Term, "GetTermInTermSet", params);
    };
    /**
     * This method provides access to a ITermGroup by id
     *
     * @param id The group id
     */
    TermStore.prototype.getTermGroupById = function (id) {
        var params = MethodParams.build()
            .string(sanitizeGuid(id));
        return this.getChild(TermGroup, "GetGroup", params);
    };
    /**
     * Gets the terms by the supplied information (see: https://msdn.microsoft.com/en-us/library/hh626704%28v=office.12%29.aspx)
     *
     * @param info
     */
    TermStore.prototype.getTerms = function (info) {
        var objectPaths = this._objectPaths.copy();
        // this will be the parent of the GetTerms call, but we need to create the input param first
        var parentIndex = objectPaths.lastIndex;
        // this is our input object
        var input = objConstructor.apply(void 0, ["{61a1d689-2744-4ea3-a88b-c95bee9803aa}",
            // actions
            objectPath()].concat(objectProperties(info)));
        // add the input object path
        var inputIndex = objectPaths.add(input);
        // this sets up the GetTerms call
        var params = MethodParams.build().objectPath(inputIndex);
        // call the method
        var methodIndex = objectPaths.add(method("GetTerms", params, 
        // actions
        objectPath()));
        // setup the parent relationship even though they are seperated in the collection
        objectPaths.addChildRelationship(parentIndex, methodIndex);
        return new Terms(this, objectPaths);
    };
    /**
     * Gets the site collection group associated with the current site
     *
     * @param createIfMissing If true the group will be created, otherwise null (default: false)
     */
    TermStore.prototype.getSiteCollectionGroup = function (createIfMissing) {
        if (createIfMissing === void 0) { createIfMissing = false; }
        var objectPaths = this._objectPaths.copy();
        var methodParent = objectPaths.lastIndex;
        var siteIndex = objectPaths.siteIndex;
        var params = MethodParams.build().objectPath(siteIndex).boolean(createIfMissing);
        var methodIndex = objectPaths.add(method("GetSiteCollectionGroup", params, 
        // actions
        objectPath()));
        // the parent of this method call is this instance, not the current/site
        objectPaths.addChildRelationship(methodParent, methodIndex);
        return new TermGroup(this, objectPaths);
    };
    /**
     * Adds a working language to the TermStore
     *
     * @param lcid The locale identifier of the working language to add
     */
    TermStore.prototype.addLanguage = function (lcid) {
        var params = MethodParams.build().number(lcid);
        return this.invokeNonQuery("AddLanguage", params);
    };
    /**
     * Creates a new Group in this TermStore
     *
     * @param name The name of the new Group being created
     * @param id The ID (Guid) that the new group should have
     */
    TermStore.prototype.addGroup = function (name, id) {
        var _this = this;
        if (id === void 0) { id = getGUID(); }
        var params = MethodParams.build()
            .string(name)
            .string(sanitizeGuid(id));
        this._useCaching = false;
        return this.invokeMethod("CreateGroup", params)
            .then(function (r) { return extend(_this.getTermGroupById(r.Id), r); });
    };
    /**
     * Commits all updates to the database that have occurred since the last commit or rollback
     */
    TermStore.prototype.commitAll = function () {
        return this.invokeNonQuery("CommitAll");
    };
    /**
     * Delete a working language from the TermStore
     *
     * @param lcid locale ID for the language to be deleted
     */
    TermStore.prototype.deleteLanguage = function (lcid) {
        var params = MethodParams.build().number(lcid);
        return this.invokeNonQuery("DeleteLanguage", params);
    };
    /**
     * Discards all updates that have occurred since the last commit or rollback
     */
    TermStore.prototype.rollbackAll = function () {
        return this.invokeNonQuery("RollbackAll");
    };
    /**
     * Updates the cache
     */
    TermStore.prototype.updateCache = function () {
        return this.invokeNonQuery("UpdateCache");
    };
    /**
     * Updates the specified properties of this term set, not all properties can be updated
     *
     * @param properties Plain object representing the properties and new values to update
     */
    TermStore.prototype.update = function (properties) {
        return this.invokeUpdate(properties, TermStore);
    };
    /**
     * This method makes sure that this instance is aware of all child terms that are used in the current site collection
     */
    TermStore.prototype.updateUsedTermsOnSite = function () {
        var objectPaths = this._objectPaths.copy();
        var methodParent = objectPaths.lastIndex;
        var siteIndex = objectPaths.siteIndex;
        var params = MethodParams.build().objectPath(siteIndex);
        var methodIndex = objectPaths.add(method("UpdateUsedTermsOnSite", params));
        // the parent of this method call is this instance, not the current context/site
        objectPaths.addChildRelationship(methodParent, methodIndex);
        return this.send(objectPaths);
    };
    /**
     * Gets a list of changes
     *
     * @param info Lookup information
     */
    TermStore.prototype.getChanges = function (info) {
        var objectPaths = this._objectPaths.copy();
        var methodParent = objectPaths.lastIndex;
        var inputIndex = objectPaths.add(objConstructor.apply(void 0, ["{1f849fb0-4fcb-4a54-9b01-9152b9e482d3}",
            // actions
            objectPath()].concat(objectProperties(info))));
        var params = MethodParams.build().objectPath(inputIndex);
        var methodIndex = objectPaths.add(method("GetChanges", params, 
        // actions
        objectPath(), opQuery([], this.getSelects())));
        objectPaths.addChildRelationship(methodParent, methodIndex);
        return this.send(objectPaths);
    };
    return TermStore;
}(ClientSvcQueryable));
export { TermStore };
//# sourceMappingURL=termstores.js.map